import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, Search, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO, { PageSEO } from '../components/SEO';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5 flex flex-col">
      <SEO {...PageSEO.notFound} url="https://squidemy.com" />
      
      <Navigation />
      
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="max-w-3xl mx-auto text-center">
          {/* Giant Squid 404 Illustration */}
          <div className="relative mb-6">
            {/* Background 404 Text */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[200px] sm:text-[280px] font-black text-primary/5 leading-none select-none">
                404
              </span>
            </div>
            
            {/* Giant Animated Squid */}
            <div className="relative z-10 flex justify-center">
              <svg viewBox="0 0 200 260" className="w-48 h-64 sm:w-64 sm:h-80 drop-shadow-2xl" style={{ animation: 'float 4s ease-in-out infinite' }}>
                <defs>
                  <linearGradient id="squidBody404" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#7C3AED', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#6366F1', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 1 }} />
                  </linearGradient>
                  <linearGradient id="squidHighlight" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#A855F7', stopOpacity: 0.5 }} />
                    <stop offset="100%" style={{ stopColor: '#7C3AED', stopOpacity: 0 }} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Squid Body */}
                <ellipse cx="100" cy="80" rx="55" ry="65" fill="url(#squidBody404)" filter="url(#glow)" />
                <ellipse cx="100" cy="70" rx="45" ry="40" fill="url(#squidHighlight)" />
                
                {/* Eyes - White background */}
                <ellipse cx="75" cy="70" rx="18" ry="22" fill="white" />
                <ellipse cx="125" cy="70" rx="18" ry="22" fill="white" />
                
                {/* Eye circles/glasses effect */}
                <circle cx="75" cy="70" r="20" fill="none" stroke="#1E293B" strokeWidth="3" />
                <circle cx="125" cy="70" r="20" fill="none" stroke="#1E293B" strokeWidth="3" />
                
                {/* Bridge between glasses */}
                <line x1="95" y1="70" x2="105" y2="70" stroke="#1E293B" strokeWidth="3" />
                
                {/* X Eyes (confused look) */}
                <g className="animate-pulse" style={{ animationDuration: '2s' }}>
                  <line x1="68" y1="63" x2="82" y2="77" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" />
                  <line x1="82" y1="63" x2="68" y2="77" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" />
                  <line x1="118" y1="63" x2="132" y2="77" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" />
                  <line x1="132" y1="63" x2="118" y2="77" stroke="#EF4444" strokeWidth="4" strokeLinecap="round" />
                </g>
                
                {/* Confused Mouth */}
                <path d="M85 105 Q100 95 115 105" fill="none" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
                
                {/* Tentacles - Animated wavy */}
                <g className="tentacles">
                  <path d="M45 140 Q35 165 45 190 Q55 215 40 240" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite' }} />
                  <path d="M65 145 Q55 175 65 200 Q75 225 60 250" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.2s' }} />
                  <path d="M85 148 Q80 180 85 210 Q90 235 82 258" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.4s' }} />
                  <path d="M100 150 Q100 185 100 215 Q100 240 100 260" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.1s' }} />
                  <path d="M115 148 Q120 180 115 210 Q110 235 118 258" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.3s' }} />
                  <path d="M135 145 Q145 175 135 200 Q125 225 140 250" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.5s' }} />
                  <path d="M155 140 Q165 165 155 190 Q145 215 160 240" fill="none" stroke="url(#squidBody404)" strokeWidth="12" strokeLinecap="round" style={{ animation: 'tentacleWave 2s ease-in-out infinite 0.15s' }} />
                </g>
                
                {/* Question marks floating */}
                <text x="25" y="45" fontSize="24" fill="#7C3AED" className="animate-bounce" style={{ animationDuration: '2s' }}>?</text>
                <text x="165" y="40" fontSize="20" fill="#3B82F6" className="animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}>?</text>
                <text x="175" y="100" fontSize="16" fill="#6366F1" className="animate-bounce" style={{ animationDelay: '1s', animationDuration: '3s' }}>?</text>
                <text x="15" y="95" fontSize="18" fill="#8B5CF6" className="animate-bounce" style={{ animationDelay: '0.7s', animationDuration: '2.2s' }}>?</text>
              </svg>
            </div>
            
            {/* Floating bubbles */}
            <div className="absolute top-10 left-1/4 w-4 h-4 bg-primary/20 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
            <div className="absolute top-20 right-1/4 w-3 h-3 bg-blue-400/30 rounded-full animate-ping" style={{ animationDelay: '1s', animationDuration: '4s' }} />
            <div className="absolute bottom-20 left-1/3 w-2 h-2 bg-purple-400/30 rounded-full animate-ping" style={{ animationDelay: '2s', animationDuration: '3.5s' }} />
          </div>

          {/* 404 Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-4">
            <span className="text-red-500 font-bold text-lg">ERROR 404</span>
          </div>

          {/* Error Message */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Oops! Page Not Found
          </h1>
          <p className="text-lg text-muted-foreground mb-2 max-w-xl mx-auto">
            The page you're looking for seems to have drifted into the deep ocean.
          </p>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            It might have been moved, deleted, or perhaps floating somewhere in the SquidTrench. That's Squidiculous!
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="lg"
              className="w-full sm:w-auto gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </Button>
            
            <Link to="/">
              <Button size="lg" className="w-full sm:w-auto gap-2 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90">
                <Home className="w-4 h-4" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Helpful Links */}
          <div className="pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-4">Looking for something specific?</p>
            <div className="flex flex-wrap gap-6 justify-center">
              <Link to="/courses" className="text-primary hover:underline flex items-center gap-2 text-sm">
                <Search className="w-4 h-4" />
                Browse Courses
              </Link>
              <Link to="/help" className="text-primary hover:underline flex items-center gap-2 text-sm">
                <HelpCircle className="w-4 h-4" />
                Get Help
              </Link>
              <Link to="/contact" className="text-primary hover:underline flex items-center gap-2 text-sm">
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        @keyframes tentacleWave {
          0%, 100% { transform: translateX(0px); }
          50% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
};

export default NotFound;
